/**
 * Utilities for STL export quality scaling.
 *
 * The quality multiplier (1x / 2x / 4x / 8x) works by scaling the $fn
 * value already present in the OpenSCAD source code.  The scaled value is
 * injected by replacing the existing `$fn = ...;` line in the source —
 * OpenSCAD's -D flag does not support special variables starting with $,
 * and for top-level special variables the last assignment wins, so the
 * safest approach is an in-place replacement of the existing line.
 * If no $fn is present, `$fn = <value>;` is appended after the last
 * parameter line (or at the top if no parameters block is found).
 */

export type ExportQuality = 1 | 2 | 4 | 8;

export const EXPORT_QUALITY_LABELS: Record<ExportQuality, string> = {
  1: '1x',
  2: '2x',
  4: '4x',
  8: '8x',
};

export const EXPORT_QUALITY_DESCRIPTIONS: Record<ExportQuality, string> = {
  1: 'Standard',
  2: 'High',
  4: 'Ultra',
  8: 'Max',
};

/** Default $fn used when none is found in the source. */
const DEFAULT_FN = 60;

/**
 * Extract the top-level `$fn = <value>;` assignment from OpenSCAD source.
 * Returns the numeric value, or null if $fn is not defined in the code.
 *
 * Uses the same regex pattern as parameterUtils.ts so it handles the same
 * syntax (integer or float literals, optional inline comments).
 */
export function extractFnValue(code: string): number | null {
  // Match top-level $fn = <numeric literal>; lines.
  // We deliberately avoid matching inside strings or block comments — a
  // simple line-by-line scan is enough for the common cases.
  const lines = code.split('\n');
  for (const line of lines) {
    // Strip line comments before matching
    const stripped = line.replace(/\/\/.*$/, '').trim();
    const match = stripped.match(/^\$fn\s*=\s*([\d.]+)\s*;/);
    if (match) {
      const value = parseFloat(match[1]);
      if (!isNaN(value) && value > 0) {
        return value;
      }
    }
  }
  return null;
}

/**
 * Return the concrete $fn integer to use for a given quality multiplier.
 *
 * - If $fn is defined in the code, multiply it by the multiplier.
 * - If $fn is not defined, fall back to sensible defaults:
 *   1x=60, 2x=120, 4x=240, 8x=480.
 * - The result is always rounded to an integer and clamped to a minimum of 1.
 */
export function getExportFn(code: string, multiplier: ExportQuality): number {
  const baseFn = extractFnValue(code) ?? DEFAULT_FN;
  return Math.max(1, Math.round(baseFn * multiplier));
}

/**
 * Return a copy of `code` with the $fn value replaced by the scaled value
 * appropriate for `multiplier`.
 *
 * Strategy:
 * 1. If a `$fn = ...;` line exists, replace its value in-place (preserving
 *    any inline comment after the semicolon).
 * 2. If no $fn line exists, prepend `$fn = <value>;` at the top of the file
 *    (no existing assignment means there's nothing to conflict with).
 */
export function injectExportFn(
  code: string,
  multiplier: ExportQuality,
): string {
  const exportFn = getExportFn(code, multiplier);
  const lines = code.split('\n');
  let replaced = false;

  const newLines = lines.map((line) => {
    if (replaced) return line;
    // Match `$fn = <value>;` optionally followed by a comment
    const match = line.match(/^(\s*\$fn\s*=\s*)[\d.]+(\s*;.*)$/);
    if (match) {
      replaced = true;
      return `${match[1]}${exportFn}${match[2]}`;
    }
    return line;
  });

  if (!replaced) {
    // No existing $fn — prepend it
    newLines.unshift(`$fn = ${exportFn};`);
  }

  return newLines.join('\n');
}
